import {
  UTM_PARAMETERS,
  CLICK_ID_PARAMETERS,
  FIELD_LIMITS,
  ATTRIBUTION_COOKIE_DOMAIN_SUFFIX,
} from "./constants.js";

/**
 * Pure URL → touch parsing. No storage, no consent, no DOM access — every input
 * is passed in, so the whole of attribution's trickiest logic is testable
 * without a browser.
 *
 * This is the layer that decides whether an arrival carries campaign signal at
 * all. Everything downstream trusts that decision, so it is deliberately
 * conservative: a touch exists only when there is something real to record.
 */

/**
 * Trim a value and enforce its abuse ceiling.
 *
 * Returns `undefined` rather than `""` for anything empty, so the caller's
 * "was there a value?" test is a simple presence check and empty strings never
 * reach storage — an empty `utm_source=` in a URL means the same as no
 * `utm_source` at all, and storing it would make a record look like a campaign.
 *
 * @param {string | null | undefined} value
 * @param {number} limit
 * @returns {string | undefined}
 */
function clean(value, limit) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, limit);
}

/**
 * Whether a referrer belongs to us.
 *
 * Our own surfaces refer to each other constantly — the landing page links to
 * the app, the invitation links back. Treating those as inbound touches would
 * manufacture an "agapae.gr" acquisition channel and overwrite a real campaign's
 * last touch with our own navigation. Same registrable-domain test the consent
 * foundation uses for cookie scoping.
 *
 * @param {string} referrerHostname
 * @param {string} [currentHostname]
 * @returns {boolean}
 */
function isInternalReferrer(referrerHostname, currentHostname) {
  if (!referrerHostname) return false;
  if (currentHostname && referrerHostname === currentHostname) return true;
  return (
    referrerHostname === ATTRIBUTION_COOKIE_DOMAIN_SUFFIX ||
    referrerHostname.endsWith(`.${ATTRIBUTION_COOKIE_DOMAIN_SUFFIX}`)
  );
}

/**
 * Reduce a referrer URL to its ORIGIN (scheme + host).
 *
 * Deliberately lossy. A full referring URL can carry another site's query
 * string, which may contain personal data we have no basis to hold and no use
 * for — channel classification only ever needs the host. Returns `undefined`
 * for an unparseable referrer, an internal one, or a non-HTTP scheme.
 *
 * @param {string | null | undefined} referrer
 * @param {string} [currentHostname]
 * @returns {string | undefined}
 */
export function parseReferrerOrigin(referrer, currentHostname) {
  const raw = clean(referrer, 2048);
  if (!raw) return undefined;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }

  // `android-app://`, `file://` and friends carry no channel meaning.
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
  if (isInternalReferrer(url.hostname, currentHostname)) return undefined;

  return clean(url.origin, FIELD_LIMITS.referrer);
}

/**
 * Parse an arrival into a touch, or `null` when it carries no campaign signal.
 *
 * A touch exists when the URL carries any UTM, any advertising click
 * identifier, or the visitor came from an external site. Everything else — a
 * bookmark, a typed address, a link from our own pages — is `null`, which the
 * caller reports as `direct`.
 *
 * `landingPath` is stored WITHOUT its query string: the campaign parameters have
 * already been parsed into their own fields, and re-embedding them would double
 * the payload and smuggle raw click identifiers past the field limits.
 *
 * @param {object} input
 * @param {string} [input.search]     `location.search`, with or without `?`.
 * @param {string} [input.referrer]   `document.referrer`.
 * @param {string} [input.pathname]   `location.pathname`.
 * @param {string} [input.hostname]   `location.hostname`, for the internal-referrer test.
 * @param {Date}   [input.now]        Injectable clock, for deterministic tests.
 * @returns {import("./types.js").AttributionTouch | null}
 */
export function parseTouch({ search = "", referrer = "", pathname = "", hostname = "", now } = {}) {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  /** @type {import("./types.js").AttributionTouch} */
  const touch = {};
  let hasCampaignSignal = false;

  for (const [param, field] of Object.entries(UTM_PARAMETERS)) {
    const value = clean(params.get(param), FIELD_LIMITS.utm);
    if (value !== undefined) {
      touch[field] = value;
      hasCampaignSignal = true;
    }
  }

  /** @type {import("./types.js").AttributionClickIds} */
  const clickIds = {};
  for (const [param, field] of Object.entries(CLICK_ID_PARAMETERS)) {
    const value = clean(params.get(param), FIELD_LIMITS.clickId);
    if (value !== undefined) {
      clickIds[field] = value;
      hasCampaignSignal = true;
    }
  }
  if (Object.keys(clickIds).length > 0) touch.clickIds = clickIds;

  const referrerOrigin = parseReferrerOrigin(referrer, hostname);
  if (referrerOrigin !== undefined) {
    touch.referrer = referrerOrigin;
    hasCampaignSignal = true;
  }

  if (!hasCampaignSignal) return null;

  const landingPath = clean(pathname, FIELD_LIMITS.landingPath);
  if (landingPath !== undefined) touch.landingPath = landingPath;

  touch.timestamp = (now instanceof Date ? now : new Date()).toISOString();
  return touch;
}
